import { z } from "zod";
/* Self-reported symptom check-ins.
 *
 * Two forms that submit independently and land in one dated history: a quick
 * energy/mood check-in and the full symptom checklist. Recorded as answered
 * and never scored — there is no severity scale, no aggregate, no derived
 * field anywhere in this file, and there must not be one added (root
 * CLAUDE.md "The software does not interpret"; compliance/roadmap.md's
 * capability table; REQ-MDR-002).
 *
 * The one exception: the questionnaire summary shows latest-vs-baseline
 * differences (new/dropped symptoms, energy and mood change in points),
 * authorised by Ville on 2026-10-09. They are computed client-side at render
 * time (apps/client/src/summary/) and stored nowhere, so the "no derived field"
 * rule still holds here.
 */
/* The checklist wording lives in the contract, not in the client app.
 *
 * It is not UI decor: the exact question and the exact labels, pinned to a
 * version number stored alongside every answer, are what make a row from
 * today still interpretable after the list is edited. The clinic app will
 * need the same labels to render a check-in, which is the second reason this
 * is shared rather than app-local.
 *
 * Bump SYMPTOM_CHECKLIST_VERSION whenever an id is added or removed, or a
 * label's meaning changes. Never reuse an id for a different symptom.
 */
export const SYMPTOM_CHECKLIST_VERSION = 1;
export const SYMPTOM_QUESTION = "Oletko viimeisen viikon aikana kärsinyt seuraavista oireista?";
export const SYMPTOM_CHECKLIST = [
    { id: "fatigue", label: "Väsymys" },
    { id: "weakness", label: "Heikotus" },
    { id: "feeling_cold", label: "Palelu" },
    { id: "cold_hands_feet", label: "Kylmät kädet ja jalat" },
    { id: "brain_fog", label: "Aivosumu" },
    { id: "poor_concentration", label: "Keskittymisvaikeudet" },
    { id: "mood_swings", label: "Mielialavaihtelut" },
    { id: "irritability", label: "Ärtyneisyys" },
    { id: "headache", label: "Päänsärky" },
    { id: "dizziness", label: "Huimaus" },
    { id: "hair_loss", label: "Hiustenlähtö" },
    { id: "brittle_nails", label: "Hauraat kynnet" },
    { id: "pallor", label: "Kalpeus" },
    { id: "sore_mouth", label: "Kipeä tai arka suu" },
    { id: "chest_pain", label: "Rintakipu" },
    { id: "palpitations", label: "Sydämentykytys" },
    { id: "breathlessness", label: "Hengästyminen" },
    { id: "poor_exercise_recovery", label: "Huono palautuminen liikunnasta" },
    { id: "muscle_cramps", label: "Lihaskrampit" },
    { id: "low_immunity", label: "Alhainen vastustuskyky" },
    { id: "insomnia", label: "Unettomuus" },
    { id: "restless_legs", label: "Levottomat jalat" },
];
// Derived from SYMPTOM_CHECKLIST rather than written out a second time, so
// the enum cannot drift from the list the client renders.
export const symptomIdSchema = z.enum(SYMPTOM_CHECKLIST.map((entry) => entry.id));
export const symptomCheckinKindSchema = z.enum(["quick", "full"]);
/** Cap on the free-text field, mirrored by the column and by the textarea's
    maxLength. Generous — this is the client telling us something the list has
    no box for, and truncating that mid-sentence loses clinical context. */
export const SYMPTOM_FREE_TEXT_MAX = 2000;
// Sender-generated idempotency key, same contract as chat's clientMessageId:
// a retry after a lost response returns the original check-in rather than
// recording a second one.
const clientCheckinIdSchema = z.string().min(1).max(64);
// The two arms are plain objects so they can go into a discriminatedUnion —
// zod 3 only accepts ZodObject options there, and a .refine() would wrap each
// in a ZodEffects. The cross-field rules that each arm needs are therefore
// applied once to the union below, not per arm.
const quickCheckinFields = z.object({
    kind: z.literal("quick"),
    clientCheckinId: clientCheckinIdSchema,
    // Null means the client did not move that slider. It is "no answer", never
    // a midpoint — a slider parked at its default 50 would otherwise be
    // indistinguishable from a deliberate "middling", which is a different
    // statement about how someone feels.
    energy: z.number().int().min(0).max(100).nullable(),
    mood: z.number().int().min(0).max(100).nullable(),
});
const fullCheckinFields = z.object({
    kind: z.literal("full"),
    clientCheckinId: clientCheckinIdSchema,
    // Rejecting a stale version is deliberate: a client running an old bundle
    // would otherwise submit answers to a question that has since changed
    // wording, and the row would claim to answer the current one.
    checklistVersion: z.literal(SYMPTOM_CHECKLIST_VERSION),
    symptoms: z.array(symptomIdSchema).max(SYMPTOM_CHECKLIST.length),
    // "Ei mitään näistä". Its own field rather than a sentinel id, so
    // "answered, nothing to report" stays distinguishable from "answered with
    // free text only" in the stored row.
    noneOfThese: z.boolean(),
    freeText: z.string().max(SYMPTOM_FREE_TEXT_MAX).nullable(),
});
// Discriminated on `kind` so the two forms' validation lives in one place and
// one route can take either. The api client still exposes them as two methods,
// because they are two flows to every caller.
export const symptomCheckinRequestSchema = z
    .discriminatedUnion("kind", [quickCheckinFields, fullCheckinFields])
    .superRefine((v, ctx) => {
    if (v.kind === "quick" && v.energy === null && v.mood === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["energy"],
            message: "A quick check-in needs at least one of energy or mood",
        });
    }
    if (v.kind === "full" && (v.noneOfThese ? v.symptoms.length > 0 : v.symptoms.length === 0)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["symptoms"],
            message: "Select at least one symptom, or 'none of these' — not both, not neither",
        });
    }
});
// The created check-in, echoed back. Deliberately thin: it exists to confirm
// the write, not to read anything back. Nothing the client submitted is
// returned, because nothing in this pass re-displays it.
export const symptomCheckinSchema = z.object({
    id: z.string(),
    kind: symptomCheckinKindSchema,
    createdAt: z.string(),
});
